import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { createSelectColumn, DataTable, SortableHeader } from '~/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import type { RosterEntryFromQuery } from './classDetailTypes';
import { useClassHealthRosterQuery } from './useClassHealthRosterQuery';

const SENSITIVE_INFORMATION_NOTICE =
  'Sensitive student information. Use only for school operations, keep printed copies secure, and destroy them when no longer needed.';

interface ClassHealthRosterProps {
  classOfferingId: string;
  activeEntries: RosterEntryFromQuery[];
  droppedEntries: RosterEntryFromQuery[];
  isAdmin: boolean;
  onDropStudent: (entry: RosterEntryFromQuery) => void;
}

interface RosterRow {
  id: string;
  entry: RosterEntryFromQuery;
  status: 'ACTIVE' | 'DROPPED';
  healthRosterEligible: boolean;
  search: string;
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'Not on file';

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(year, month - 1, day));
}

function listedAllergies(value: string | null | undefined) {
  return value?.trim() || 'Not listed';
}

function listedMedicalCondition(value: string | null | undefined) {
  return value?.trim() || 'Not listed';
}

export default function ClassHealthRoster({
  classOfferingId,
  activeEntries,
  droppedEntries,
  isAdmin,
  onDropStudent,
}: ClassHealthRosterProps) {
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [printRequested, setPrintRequested] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);
  const shouldLoadHealthRoster = selectedStudentId !== null || printRequested;
  const { data, loading, error } = useClassHealthRosterQuery(
    classOfferingId,
    shouldLoadHealthRoster
  );
  const healthRoster = data?.classHealthRoster;
  const healthEligibleActiveEntries = useMemo(
    () => activeEntries.filter((entry) => entry.healthRosterEligible),
    [activeEntries]
  );
  const healthRosterMatchesEligibleEntries = useMemo(() => {
    if (!healthRoster || healthRoster.students.length !== healthEligibleActiveEntries.length) return false;

    const expectedStudentIds = new Set(healthEligibleActiveEntries.map((entry) => entry.person.id));
    const returnedStudentIds = new Set(healthRoster.students.map((student) => student.studentId));
    return expectedStudentIds.size === returnedStudentIds.size &&
      [...expectedStudentIds].every((studentId) => returnedStudentIds.has(studentId));
  }, [healthEligibleActiveEntries, healthRoster]);
  const selectedStudent = useMemo(
    () => healthRosterMatchesEligibleEntries
      ? healthRoster?.students.find((student) => student.studentId === selectedStudentId)
      : undefined,
    [healthRoster, healthRosterMatchesEligibleEntries, selectedStudentId]
  );
  const rosterRows = useMemo<RosterRow[]>(() => [
    ...activeEntries.map((entry) => ({
      id: entry.id,
      entry,
      status: 'ACTIVE' as const,
      healthRosterEligible: entry.healthRosterEligible,
      search: `${entry.person.firstName} ${entry.person.lastName}`,
    })),
    ...droppedEntries.map((entry) => ({
      id: entry.id,
      entry,
      status: 'DROPPED' as const,
      healthRosterEligible: false,
      search: `${entry.person.firstName} ${entry.person.lastName}`,
    })),
  ], [activeEntries, droppedEntries]);
  const rosterColumns = useMemo<ColumnDef<RosterRow>[]>(() => [
    createSelectColumn<RosterRow>(),
    {
      accessorKey: 'search',
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      // Sort the way the cell reads: last name first.
      meta: {
        sortKey: (row: RosterRow) =>
          `${row.entry.person.lastName} ${row.entry.person.firstName}`,
      },
      cell: ({ row }) => {
        const { entry, healthRosterEligible: canViewHealthRoster, status } = row.original;
        const fullName = `${entry.person.firstName} ${entry.person.lastName}`;

        if (status === 'DROPPED' || !canViewHealthRoster) {
          return (
            <span className={`font-medium ${status === 'DROPPED' ? 'line-through' : ''}`}>
              {entry.person.lastName}, {entry.person.firstName}
            </span>
          );
        }

        return (
          <button
            type="button"
            data-no-row-click
            className="rounded-sm text-left font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={() => {
              setHealthError(null);
              setSelectedStudentId(entry.person.id);
            }}
            aria-label={`View DOB and medical information for ${fullName}`}
          >
            {entry.person.lastName}, {entry.person.firstName}
          </button>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => row.original.status === 'ACTIVE'
        ? <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>
        : <Badge variant="secondary">Dropped</Badge>,
      meta: {
        facet: {
          title: 'Status',
          getKey: (rosterRow: RosterRow) => rosterRow.status,
          getLabel: (rosterRow: RosterRow) =>
            rosterRow.status === 'ACTIVE' ? 'Active' : 'Dropped',
        },
      },
    },
    {
      id: 'enrolledOn',
      header: 'Enrolled',
      cell: ({ row }) => String(
        row.original.status === 'DROPPED'
          ? row.original.entry.droppedOn || row.original.entry.enrolledOn || ''
          : row.original.entry.enrolledOn ?? ''
      ),
    },
    ...(isAdmin ? [{
      id: 'actions',
      header: '',
      cell: ({ row }: { row: { original: RosterRow } }) => row.original.status === 'ACTIVE' ? (
        <div className="text-right" data-no-row-click>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => onDropStudent(row.original.entry)}
            aria-label={`Drop ${row.original.entry.person.firstName} ${row.original.entry.person.lastName}`}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : null,
      enableSorting: false,
      enableHiding: false,
    } as ColumnDef<RosterRow>] : []),
  ], [isAdmin, onDropStudent]);

  useEffect(() => {
    if (!printRequested || loading) return;

    if (error || !healthRoster || !healthRosterMatchesEligibleEntries) {
      setHealthError('DOB and medical information could not be refreshed for the active roster.');
      setPrintRequested(false);
      return;
    }

    const leavePrintMode = () => document.body.classList.remove('printing-health-roster');
    const handleAfterPrint = () => {
      leavePrintMode();
      setPrintRequested(false);
    };

    document.body.classList.add('printing-health-roster');
    window.addEventListener('afterprint', handleAfterPrint, { once: true });
    const printTimer = window.setTimeout(() => {
      try {
        window.print();
      } catch {
        window.removeEventListener('afterprint', handleAfterPrint);
        leavePrintMode();
        setHealthError('The browser print dialog could not be opened.');
        setPrintRequested(false);
      }
    }, 0);

    return () => {
      window.clearTimeout(printTimer);
      window.removeEventListener('afterprint', handleAfterPrint);
      leavePrintMode();
    };
  }, [error, healthRoster, healthRosterMatchesEligibleEntries, loading, printRequested]);

  useEffect(() => () => {
    document.body.classList.remove('printing-health-roster');
  }, []);

  const requestPrint = () => {
    setHealthError(null);
    setPrintRequested(true);
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 print:hidden">
        <p className="text-xs text-muted-foreground">Select a student to view DOB and medical information.</p>
        {healthEligibleActiveEntries.length > 0 && (
          <Button variant="outline" size="sm" onClick={requestPrint} disabled={printRequested}>
            <Printer className="h-4 w-4" />
            {printRequested ? 'Preparing roster…' : 'Print DOB & medical roster'}
          </Button>
        )}
      </div>

      {healthError && (
        <p role="alert" className="mb-3 text-sm text-destructive print:hidden">{healthError}</p>
      )}

      <div className="print:hidden">
        <DataTable
          columns={rosterColumns}
          data={rosterRows}
          enableRowSelection
          searchColumn="search"
          searchPlaceholder="Search roster..."
          defaultSorting={[{ id: 'search', desc: false }]}
          stateKey={`class-roster-${classOfferingId}`}
        />
      </div>

      <Dialog
        open={selectedStudentId !== null}
        onOpenChange={(open) => { if (!open) setSelectedStudentId(null); }}
      >
        <DialogContent className="max-w-md print:hidden">
          <DialogHeader>
            <DialogTitle>
              {selectedStudent
                ? `${selectedStudent.firstName} ${selectedStudent.lastName}`
                : 'Student health details'}
            </DialogTitle>
            <DialogDescription>{SENSITIVE_INFORMATION_NOTICE}</DialogDescription>
          </DialogHeader>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading DOB and medical information…</p>
          ) : error ? (
            <p role="alert" className="text-sm text-destructive">DOB and medical information could not be loaded.</p>
          ) : !healthRosterMatchesEligibleEntries ? (
            <p role="alert" className="text-sm text-destructive">
              DOB and medical information could not be refreshed for the active roster.
            </p>
          ) : selectedStudent ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 rounded-lg border p-4 text-sm">
              <dt className="font-medium">Date of birth</dt>
              <dd>{formatDate(selectedStudent.dateOfBirth as string | null)}</dd>
              <dt className="font-medium">Medical condition</dt>
              <dd className="whitespace-pre-wrap">{listedMedicalCondition(selectedStudent.medicalCondition)}</dd>
              <dt className="font-medium">Allergies</dt>
              <dd className="whitespace-pre-wrap">{listedAllergies(selectedStudent.allergies)}</dd>
            </dl>
          ) : (
            <p role="alert" className="text-sm text-destructive">This student is not on the active roster.</p>
          )}
        </DialogContent>
      </Dialog>

      {printRequested && healthRoster && healthRosterMatchesEligibleEntries && createPortal(
        <section className="health-roster-print" aria-label="Printable DOB and medical roster">
          <h1>{healthRoster.displayName}</h1>
          <h2>DOB &amp; medical roster</h2>
          <p className="health-roster-print__notice">{SENSITIVE_INFORMATION_NOTICE}</p>
          <table>
            <thead>
              <tr>
                <th>Student</th>
                <th>Date of birth</th>
                <th>Medical condition</th>
                <th>Allergies</th>
              </tr>
            </thead>
            <tbody>
              {healthRoster.students.map((student) => (
                <tr key={student.studentId}>
                  <td>{student.lastName}, {student.firstName}</td>
                  <td>{formatDate(student.dateOfBirth as string | null)}</td>
                  <td>{listedMedicalCondition(student.medicalCondition)}</td>
                  <td>{listedAllergies(student.allergies)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="health-roster-print__footer">Printed from SolaStack for authorized school use.</p>
        </section>,
        document.body
      )}
    </>
  );
}
